import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, Checkbox, Column, Row, TextButton, Tooltip, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useCobConfig } from '~/client-flow/clientOb/CobConfigContext';
import { MobileSign } from '~/client-flow/clientOb/components/MobileSign';
import { useResponsive } from '~/client-flow/contexts';
import BankSecurityRow from '~/components/BankSecurityRow/BankSecurityRow';
import { useStore } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { relationshipModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { COBStep, reasonToText } from '../../../ClientOnboarding.utils';
import { getNextStepButtonText, nextStepButtonTextsPath } from '../../ClientOnboardingV2.utils';
import { useSignAndApproveContext } from '../../hooks/useSignAndApprove';
import { GetPaymentMethodTID } from '../Steps/PaymentMethod/PaymentMethod';
import desktopStyles from '../Steps/StepsContainer/StepsContainerDesktop.module.scss';
import styles from './Footer.module.scss';
const ANCHOR_TOS_URL = 'https://www.sayanchor.com/terms-of-contact';
const collapseVariants = {
    hidden: {
        height: 0,
        opacity: 0,
        marginBottom: 0
    },
    visible: {
        height: 'auto',
        opacity: 1,
        marginBottom: 12
    }
};
const collapseTransition = {
    duration: 0.3,
    ease: 'easeInOut'
};
export const GetFooterTID = (parentTestId)=>getTestIds({
        parentTestId
    });
export const Footer = (param)=>{
    let { currentStep, relationship, onNext, testId, isNextDisabled, hideNextButton, statement, isStatementAccepted, onStatementChange } = param;
    const { t: footerTexts } = useLocaleKeys('clientFlow.footer');
    const { t: clientFlowTexts } = useLocaleKeys(nextStepButtonTextsPath);
    const { isMobile } = useResponsive();
    const { CUSTOM_PAGES } = useFeatureFlags();
    const { FooterTestIds } = useMemo(()=>GetFooterTID(testId), [
        testId
    ]);
    const { PaymentMethodTestIds } = useMemo(()=>GetPaymentMethodTID(), []);
    const nextStepButtonText = getNextStepButtonText(currentStep, relationship, clientFlowTexts, {
        customPagesFFEnabled: !!CUSTOM_PAGES
    });
    const showCheckbox = statement && onStatementChange;
    const isSignAndApproveStep = currentStep === COBStep.signAndApprove;
    const { signature, disabledReason } = useSignAndApproveContext();
    const { isPreview } = useCobConfig();
    const userId = useStore(selectors.user.id);
    const isNonSignatory = relationshipModel.isReviewer(relationship, userId);
    const hasSignature = !!signature;
    const showTermsText = hasSignature && !isPreview;
    const showAcceptTermsHint = !!showCheckbox && !isStatementAccepted;
    const disabledReasonText = disabledReason ? reasonToText[disabledReason] : null;
    if (hideNextButton) {
        return /*#__PURE__*/ _jsx("div", {
            className: isMobile ? styles.mobileFooterSpacer : styles.footerSpacer
        });
    }
    if (isMobile) {
        const showBankSecurityRow = currentStep === COBStep.paymentMethod;
        return /*#__PURE__*/ _jsx(motion.div, {
            className: styles.mobileFooter,
            "data-testid": FooterTestIds.Root(),
            layout: true,
            transition: collapseTransition,
            children: /*#__PURE__*/ _jsxs(Column, {
                className: cn(styles.mobileFooterContent, isSignAndApproveStep && styles.mobileFooterContentCentered),
                gap: isSignAndApproveStep ? 0 : 16,
                children: [
                    isSignAndApproveStep && /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph3",
                        skin: "secondary",
                        weight: "light",
                        className: cn(styles.signAndApproveHint, styles.signAndApproveHintCentered),
                        children: showTermsText ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                footerTexts.byApprovingAgreement(),
                                /*#__PURE__*/ _jsx(TextButton, {
                                    as: "a",
                                    href: ANCHOR_TOS_URL,
                                    target: "_blank",
                                    rel: "noopener noreferrer",
                                    variant: "paragraph3",
                                    children: footerTexts.termsOfService()
                                }),
                                "."
                            ]
                        }) : isNonSignatory ? footerTexts.notRequiredToSign() : footerTexts.signAboveToApprove()
                    }),
                    /*#__PURE__*/ _jsx(AnimatePresence, {
                        initial: false,
                        children: showCheckbox && /*#__PURE__*/ _jsx(motion.div, {
                            variants: collapseVariants,
                            initial: "hidden",
                            animate: "visible",
                            exit: "hidden",
                            transition: collapseTransition,
                            style: {
                                overflow: 'hidden'
                            },
                            children: /*#__PURE__*/ _jsx(Checkbox, {
                                className: styles.statementCheckbox,
                                onChange: (e)=>onStatementChange(e.target.checked),
                                testId: FooterTestIds.StatementCheckbox(),
                                checked: isStatementAccepted,
                                children: /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph3",
                                    weight: "light",
                                    children: statement
                                })
                            })
                        }, "checkbox-collapse")
                    }),
                    showBankSecurityRow && /*#__PURE__*/ _jsx(BankSecurityRow, {
                        mobileView: true,
                        className: styles.bankSecurityRow,
                        "data-testid": PaymentMethodTestIds.BankSecurityRow()
                    }),
                    isSignAndApproveStep && disabledReasonText && /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph3",
                        skin: "secondary",
                        weight: "light",
                        className: styles.signAndApproveHint,
                        testId: FooterTestIds.DisabledReason(),
                        children: disabledReasonText
                    }),
                    isSignAndApproveStep ? /*#__PURE__*/ _jsx("div", {
                        className: styles.mobileSignWrapper,
                        "data-testid": FooterTestIds.NextButton(),
                        children: /*#__PURE__*/ _jsx(MobileSign, {
                            relationship: relationship,
                            disabled: isNextDisabled,
                            onSign: onNext,
                            hideSignatoryTypography: true
                        })
                    }) : showAcceptTermsHint ? /*#__PURE__*/ _jsx(Tooltip, {
                        title: footerTexts.acceptTermsRequired(),
                        placement: "top",
                        children: /*#__PURE__*/ _jsx("div", {
                            className: cn(styles.mobileFooterButton, styles.disabledButtonWrapper),
                            "data-testid": FooterTestIds.NextButton() + '-tooltip-wrapper',
                            children: /*#__PURE__*/ _jsx(Button, {
                                onClick: onNext,
                                testId: FooterTestIds.NextButton(),
                                disabled: true,
                                fluid: true,
                                children: nextStepButtonText
                            })
                        })
                    }) : /*#__PURE__*/ _jsx(Button, {
                        onClick: onNext,
                        className: styles.mobileFooterButton,
                        testId: FooterTestIds.NextButton(),
                        disabled: isNextDisabled,
                        children: nextStepButtonText
                    })
                ]
            })
        });
    }
    const showSignAndApproveFooter = isSignAndApproveStep;
    return /*#__PURE__*/ _jsx(Row, {
        className: cn(desktopStyles.footer, {
            [styles.footerWithCheckbox]: showCheckbox,
            [styles.footerWithSignAndApprove]: showSignAndApproveFooter
        }),
        testId: FooterTestIds.Root(),
        children: /*#__PURE__*/ _jsxs(Row, {
            className: cn(styles.footerContent, {
                [styles.footerContentWithCheckbox]: showCheckbox,
                [styles.footerContentWithSignAndApprove]: showSignAndApproveFooter
            }),
            testId: FooterTestIds.FooterContent(),
            children: [
                showCheckbox && /*#__PURE__*/ _jsx(Row, {
                    className: styles.checkboxRow,
                    children: /*#__PURE__*/ _jsx(Checkbox, {
                        className: styles.statementCheckbox,
                        onChange: (e)=>onStatementChange(e.target.checked),
                        testId: FooterTestIds.StatementCheckbox(),
                        checked: isStatementAccepted,
                        children: /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            weight: "light",
                            children: statement
                        })
                    })
                }),
                showSignAndApproveFooter && /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph2",
                    skin: "secondary",
                    weight: "light",
                    className: styles.signAndApproveHint,
                    children: showTermsText ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            footerTexts.byApprovingAgreement(),
                            /*#__PURE__*/ _jsx(TextButton, {
                                as: "a",
                                href: ANCHOR_TOS_URL,
                                target: "_blank",
                                rel: "noopener noreferrer",
                                variant: "paragraph2",
                                children: footerTexts.termsOfService()
                            }),
                            "."
                        ]
                    }) : isNonSignatory ? footerTexts.notRequiredToSign() : footerTexts.signAboveToApprove()
                }),
                showSignAndApproveFooter && disabledReasonText && /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph2",
                    skin: "secondary",
                    weight: "light",
                    className: styles.signAndApproveHint,
                    testId: FooterTestIds.DisabledReason(),
                    children: disabledReasonText
                }),
                showAcceptTermsHint ? /*#__PURE__*/ _jsx(Tooltip, {
                    title: footerTexts.acceptTermsRequired(),
                    placement: "top",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: cn(desktopStyles.button, styles.disabledButtonWrapper),
                        "data-testid": FooterTestIds.NextButton() + '-tooltip-wrapper',
                        children: /*#__PURE__*/ _jsx(Button, {
                            onClick: onNext,
                            testId: FooterTestIds.NextButton(),
                            disabled: true,
                            fluid: true,
                            children: nextStepButtonText
                        })
                    })
                }) : /*#__PURE__*/ _jsx(Button, {
                    onClick: onNext,
                    className: desktopStyles.button,
                    testId: FooterTestIds.NextButton(),
                    disabled: isNextDisabled,
                    children: nextStepButtonText
                })
            ]
        })
    });
};
